import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowLeft,
  FiShield,
  FiLock,
  FiKey,
  FiUsers,
  FiLink,
  FiEye,
  FiClock,
  FiAlertTriangle,
  FiCheckCircle,
  FiChevronDown,
  FiChevronUp,
  FiPlay,
  FiDatabase,
  FiUnlock,
  FiRefreshCw
} from "react-icons/fi";
import usePageTitle from "../../utils/hooks/usePageTitle";

// Interactive Flow Animation Component - Vertical Layout for Better Readability
const InteractiveFlowDiagram: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const steps = [
    { id: 0, label: "Create Vault", icon: FiDatabase, actor: "owner", description: "Business owner creates a secure vault to store credentials" },
    { id: 1, label: "Set Master Password", icon: FiKey, actor: "owner", description: "A strong password that only you know - never share this!" },
    { id: 2, label: "Add Credentials", icon: FiLock, actor: "owner", description: "Add passwords, API keys, and other sensitive data - encrypted instantly" },
    { id: 3, label: "Link to Program", icon: FiLink, actor: "owner", description: "Connect your vault to a specific PTaaS program" },
    { id: 4, label: "Set Access Password", icon: FiUnlock, actor: "owner", description: "Create a separate password to encrypt data for this program" }
  ];

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isPlaying) {
      interval = setInterval(() => {
        setActiveStep((prev) => (prev + 1) % steps.length);
      }, 2500);
    }
    return () => clearInterval(interval);
  }, [isPlaying, steps.length]);

  return (
    <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 text-white">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h3 className="text-xl font-semibold flex items-center gap-2 mb-2">
            <FiRefreshCw className={`w-5 h-5 ${isPlaying ? "animate-spin" : ""}`} />
            Credential Access Flow
          </h3>
          <p className="text-slate-400 text-sm">Click any step or use auto-play to see the complete flow</p>
        </div>
        <button
          onClick={() => setIsPlaying(!isPlaying)}
          className={`px-5 py-2.5 rounded-xl font-medium text-sm transition-all flex items-center gap-2 ${
            isPlaying
              ? "bg-red-500 hover:bg-red-600"
              : "bg-green-500 hover:bg-green-600"
          }`}
        >
          {isPlaying ? "⏸ Pause" : "▶ Auto Play"}
        </button>
      </div>

      {/* Actor Legend */}
      <div className="flex gap-6 mb-6">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-blue-500"></div>
          <span className="text-sm text-slate-300">Business Owner</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-purple-500"></div>
          <span className="text-sm text-slate-300">Pentester</span>
        </div>
      </div>

      {/* Steps Grid - 5 columns on desktop */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isActive = activeStep === index;
          const isPast = activeStep > index;
          const isOwner = step.actor === "owner";

          return (
            <button
              key={step.id}
              onClick={() => { setActiveStep(index); setIsPlaying(false); }}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl transition-all duration-300 ${
                isActive
                  ? isOwner
                    ? "bg-blue-500 shadow-lg shadow-blue-500/30 scale-105"
                    : "bg-purple-500 shadow-lg shadow-purple-500/30 scale-105"
                  : isPast
                    ? "bg-slate-700/50 opacity-60"
                    : "bg-slate-800 hover:bg-slate-700"
              }`}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                isActive
                  ? "bg-white/20"
                  : isOwner ? "bg-blue-500/20" : "bg-purple-500/20"
              }`}>
                <Icon className={`w-5 h-5 ${isActive ? "animate-pulse" : ""}`} />
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                isOwner ? "bg-blue-500/30 text-blue-300" : "bg-purple-500/30 text-purple-300"
              }`}>
                Step {index + 1}
              </span>
              <p className="text-xs font-medium text-center leading-tight">{step.label}</p>
            </button>
          );
        })}
      </div>

      {/* Active Step Detail - Compact */}
      <div className={`p-5 rounded-xl border transition-all duration-300 ${
        steps[activeStep].actor === "owner"
          ? "bg-blue-500/10 border-blue-500/30"
          : "bg-purple-500/10 border-purple-500/30"
      }`}>
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
            steps[activeStep].actor === "owner" ? "bg-blue-500" : "bg-purple-500"
          }`}>
            {React.createElement(steps[activeStep].icon, { className: "w-6 h-6" })}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                steps[activeStep].actor === "owner"
                  ? "bg-blue-500/30 text-blue-300"
                  : "bg-purple-500/30 text-purple-300"
              }`}>
                {steps[activeStep].actor === "owner" ? "👔 Business Owner" : "🔍 Security Team"}
              </span>
            </div>
            <h4 className="text-lg font-bold">{steps[activeStep].label}</h4>
          </div>
          <div className="text-sm text-slate-400 hidden sm:block">
            {activeStep < steps.length - 1
              ? `Next → ${steps[activeStep + 1].label}`
              : "✅ Complete!"}
          </div>
        </div>
        <p className="text-slate-300 mt-3 text-sm">{steps[activeStep].description}</p>
      </div>

      {/* Progress Bar */}
      <div className="mt-4 h-1.5 bg-slate-700 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500"
          style={{ width: `${((activeStep + 1) / steps.length) * 100}%` }}
        ></div>
      </div>
    </div>
  );
};

// Learning Progress Checklist Component
const LearningChecklist: React.FC = () => {
  const [checked, setChecked] = useState<Set<number>>(new Set());

  const items = [
    "I understand what the Credential Vault is",
    "I know the difference between Master Password and Access Password",
    "I understand how to create and manage vaults",
    "I know how to link vaults to programs",
    "I understand the security measures in place",
    "I know how to revoke access when needed"
  ];

  const toggleItem = (index: number) => {
    const newChecked = new Set(checked);
    if (newChecked.has(index)) {
      newChecked.delete(index);
    } else {
      newChecked.add(index);
    }
    setChecked(newChecked);
  };

  const progress = (checked.size / items.length) * 100;

  return (
    <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-amber-900 flex items-center gap-2">
          📋 Learning Checklist
        </h3>
        <span className="text-sm font-medium text-amber-700">
          {checked.size}/{items.length} Complete
        </span>
      </div>

      {/* Progress Bar */}
      <div className="h-2 bg-amber-200 rounded-full mb-4 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all duration-500"
          style={{ width: `${progress}%` }}
        ></div>
      </div>

      <div className="space-y-2">
        {items.map((item, index) => (
          <button
            key={index}
            onClick={() => toggleItem(index)}
            className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all text-left ${
              checked.has(index)
                ? "bg-green-100 border-2 border-green-300"
                : "bg-white border-2 border-transparent hover:border-amber-300"
            }`}
          >
            <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${
              checked.has(index)
                ? "bg-green-500 text-white"
                : "bg-gray-200 text-gray-400"
            }`}>
              {checked.has(index) ? "✓" : index + 1}
            </div>
            <span className={`text-sm ${checked.has(index) ? "text-green-800 line-through" : "text-gray-700"}`}>
              {item}
            </span>
          </button>
        ))}
      </div>

      {checked.size === items.length && (
        <div className="mt-4 p-4 bg-green-100 rounded-xl border border-green-300 text-center">
          <p className="text-green-800 font-semibold">🎉 Congratulations! You're ready to use the Credential Vault!</p>
        </div>
      )}
    </div>
  );
};

// FAQ Item Component
const FAQItem: React.FC<{ question: string; answer: React.ReactNode }> = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-5 py-4 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors text-left"
      >
        <span className="font-medium text-gray-900">{question}</span>
        {isOpen ? <FiChevronUp className="text-gray-500" /> : <FiChevronDown className="text-gray-500" />}
      </button>
      {isOpen && (
        <div className="px-5 py-4 bg-gray-50 border-t border-gray-200 text-gray-700 text-sm leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
};

// Feature Card Component
const FeatureCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  color: "blue" | "green" | "purple" | "orange";
}> = ({ icon, title, description, color }) => {
  const colorClasses = {
    blue: "bg-blue-50 border-blue-200 text-blue-600",
    green: "bg-green-50 border-green-200 text-green-600",
    purple: "bg-purple-50 border-purple-200 text-purple-600",
    orange: "bg-orange-50 border-orange-200 text-orange-600"
  };
  return (
    <div className={`rounded-xl border p-5 ${colorClasses[color]}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 bg-white rounded-lg shadow-sm">{icon}</div>
        <h3 className="font-semibold text-gray-900">{title}</h3>
      </div>
      <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
    </div>
  );
};

const CredentialVaultGuidePage: React.FC = () => {
  usePageTitle("Credential Vault Guide | Capture The Bug");

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <Link
            to="/dashboard/vault"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors text-sm font-medium"
          >
            <FiArrowLeft className="w-4 h-4" />
            Back to Credential Vault
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 text-white">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <div className="flex items-center gap-3 mb-4 ">
            <div className="p-3 bg-white/10 rounded-xl backdrop-blur-sm">
              <FiShield className="w-8 h-8" />
            </div>
        <h1 className="text-4xl font-bold">Credential Vault Guide</h1>
          </div>
        
          <p className="text-xsm text-blue-100 max-w-2xl leading-relaxed">
            Learn how to securely store, manage, and link credentials to your security testing programs 
            using our enterprise-grade encrypted vault system.
          </p>
        </div>
      </div>

      {/* Table of Contents */}
      <div className="max-w-5xl mx-auto px-6 -mt-8">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Quick Navigation</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: "Overview", href: "#overview" },
              { label: "Getting Started", href: "#getting-started" },
              { label: "Managing Vaults", href: "#managing-vaults" },
              { label: "Access Controls", href: "#access-controls" },
              { label: "FAQ", href: "#faq" }
              // { label: "Video Tutorial", href: "#video" }
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="px-4 py-2 bg-gray-50 hover:bg-blue-50 text-gray-700 hover:text-blue-700 rounded-lg text-sm font-medium transition-colors text-center"
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12 space-y-16">
        {/* Overview Section */}
        <section id="overview">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FiShield className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">What is the Credential Vault?</h2>
          </div>

          <div className="prose prose-gray max-w-none mb-8">
            <p className="text-gray-600 text-lg leading-relaxed">
              The Credential Vault is a <strong>secure, encrypted storage system</strong> designed specifically for
              managing sensitive credentials that need to be shared with pentesters and penetration testing teams.
              Think of it as a digital safe where you can store passwords, API keys, server credentials, and other
              sensitive information that your testing team needs to perform their work.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <FeatureCard
              icon={<FiLock className="w-5 h-5" />}
              title="End-to-End Encryption"
              description="All credentials are encrypted on your device before being stored. Even we cannot see your data."
              color="blue"
            />
            <FeatureCard
              icon={<FiKey className="w-5 h-5" />}
              title="Master Password Protection"
              description="Your vault is protected by a master password that only you know. Never share this password."
              color="green"
            />
            <FeatureCard
              icon={<FiUsers className="w-5 h-5" />}
              title="Secure Access"
              description="Once linked to a program, authorized researchers gain access using the program-specific access password."
              color="purple"
            />
            <FeatureCard
              icon={<FiClock className="w-5 h-5" />}
              title="Access Controls"
              description="Set expiration dates, view limits, and revoke access at any time to maintain control."
              color="orange"
            />
          </div>

          {/* Interactive Flow Diagram */}
          <div className="mt-10">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">🎬 How It Works - Interactive Flow</h3>
            <InteractiveFlowDiagram />
          </div>
        </section>

        {/* Key Concepts */}
        <section className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-8 border border-amber-200">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FiAlertTriangle className="text-amber-500" />
            Important Concepts to Understand
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-5 shadow-sm">
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-xs font-bold text-blue-600">1</span>
                Master Password
              </h4>
              <p className="text-sm text-gray-600">
                This is <strong>YOUR</strong> password to unlock the vault. It's used to encrypt and decrypt your stored credentials.
                <span className="text-red-600 font-medium"> Never share this with anyone.</span>
              </p>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-sm">
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <span className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center text-xs font-bold text-green-600">2</span>
                Access Password
              </h4>
              <p className="text-sm text-gray-600">
                This is a <strong>separate password</strong> you set when linking a vault to a program.
                <span className="text-green-600 font-medium"> It is used to securely share access without revealing your master password.</span>
              </p>
            </div>
          </div>
        </section>

        {/* Complete Step-by-Step Walkthrough */}
        <section id="walkthrough">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg">
              <FiPlay className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Step-by-Step Guide</h2>
              <p className="text-sm text-gray-500">Complete walkthrough from setup to sharing</p>
            </div>
          </div>

          {/* Timeline Style Steps */}
          <div className="relative">
            {/* Vertical Line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 via-purple-500 to-green-500 hidden md:block"></div>

            <div className="space-y-5">
              {/* Step 1 */}
              <div className="relative flex gap-4 md:gap-6">
                <div className="w-12 h-12 bg-blue-600 text-white rounded-xl flex items-center justify-center font-bold text-lg flex-shrink-0 z-10 shadow-lg">1</div>
                <div className="flex-1 bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                    <FiLock className="w-5 h-5 text-blue-600" />
                    Navigate to Credential Vault
                  </h3>
                  <div className="mt-3 space-y-2 text-sm text-gray-600">
                    <p>After logging into your business account, look at the <strong>left sidebar navigation</strong> on your dashboard.</p>
                    <p>Find and click on <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded font-medium"><FiLock className="w-3 h-3" /> Credential Vault</span> to open the credential management page.</p>
                    <p>This is your central hub where all your secure vaults and credentials will be managed.</p>
                  </div>
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-xs text-blue-700"><strong>💡 Note:</strong> If you don't see this option, verify that you have Business Owner permissions on your account.</p>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="relative flex gap-4 md:gap-6">
                <div className="w-12 h-12 bg-blue-600 text-white rounded-xl flex items-center justify-center font-bold text-lg flex-shrink-0 z-10 shadow-lg">2</div>
                <div className="flex-1 bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                    <FiShield className="w-5 h-5 text-blue-600" />
                    Create a New Vault
                  </h3>
                  <div className="mt-3 space-y-2 text-sm text-gray-600">
                    <p>On the Credential Vault page, click the <span className="px-2 py-1 bg-blue-600 text-white rounded text-xs font-medium">+ New Vault</span> button located at the top right corner.</p>
                    <p>A modal window will appear where you need to fill in:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Vault Name:</strong> A descriptive name (e.g., "Production API Credentials" or "Staging Environment")</li>
                      <li><strong>Description:</strong> Optional details about what this vault contains and its purpose</li>
                    </ul>
                    <p>Click <span className="px-2 py-1 bg-blue-600 text-white rounded text-xs font-medium">Create Vault</span> to proceed.</p>
                  </div>
                  <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-100">
                    <p className="text-xs text-amber-700"><strong>⚠️ Best Practice:</strong> Create separate vaults for different environments (Production, Staging, Development) or different projects. This keeps credentials organized and allows granular sharing control.</p>
                  </div>
                </div>
              </div>

              {/* Step 3 - Critical */}
              <div className="relative flex gap-4 md:gap-6">
                <div className="w-12 h-12 bg-blue-600 text-white rounded-xl flex items-center justify-center font-bold text-lg flex-shrink-0 z-10 shadow-lg ring-4 ring-blue-200">3</div>
                <div className="flex-1 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 shadow-sm border border-blue-200">
                  <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                    <FiKey className="w-5 h-5 text-blue-600" />
                    Set Your Master Password
                    <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-semibold">CRITICAL STEP</span>
                  </h3>
                  <div className="mt-3 space-y-2 text-sm text-gray-600">
                    <p>After creating the vault, you'll be prompted to set a <strong>Master Password</strong>. This is the most important step in the entire process.</p>
                    <p>Your Master Password is used to encrypt all credentials stored in this vault. Choose a strong, unique password that you can remember.</p>
                    <p><strong>What the Master Password does:</strong></p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li>Encrypts all credentials stored in this vault using military-grade encryption</li>
                      <li>Required every time you want to view, edit, or share credentials</li>
                      <li>Never stored on our servers — we use zero-knowledge architecture</li>
                    </ul>
                    <p>Enter your password, confirm it by typing again, then click <span className="px-2 py-1 bg-blue-600 text-white rounded text-xs font-medium">Set Password</span>.</p>
                  </div>
                  <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-xs text-red-700"><strong>🚨 Critical Warning:</strong> If you forget your master password, there is absolutely NO way to recover your encrypted credentials. We do not store your master password anywhere. Consider storing it in a personal password manager.</p>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4">
                    <span className="px-3 py-1.5 bg-white rounded-lg text-xs text-gray-700 border shadow-sm">🔒 Zero-knowledge encryption</span>
                    <span className="px-3 py-1.5 bg-white rounded-lg text-xs text-gray-700 border shadow-sm">⚠️ Cannot be recovered if lost</span>
                    <span className="px-3 py-1.5 bg-white rounded-lg text-xs text-gray-700 border shadow-sm">🚫 Never share with anyone</span>
                  </div>
                </div>
              </div>

              {/* Step 4 */}
              <div className="relative flex gap-4 md:gap-6">
                <div className="w-12 h-12 bg-blue-600 text-white rounded-xl flex items-center justify-center font-bold text-lg flex-shrink-0 z-10 shadow-lg">4</div>
                <div className="flex-1 bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                    <FiDatabase className="w-5 h-5 text-blue-600" />
                    Add Credentials to Your Vault
                  </h3>
                  <div className="mt-3 space-y-2 text-sm text-gray-600">
                    <p>With your vault created and secured, click on it to expand, then click <span className="px-2 py-1 bg-green-600 text-white rounded text-xs font-medium">+ Add Credential</span>.</p>
                    <p>You'll first be asked to enter your Master Password to unlock the vault temporarily.</p>
                    <p><strong>Fill in the credential details:</strong></p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Credential Type:</strong> Select from Password, API Key, SSH Key, Access Token, or Custom</li>
                      <li><strong>Label/Name:</strong> A clear identifier (e.g., "Admin Panel Login", "Stripe API Key")</li>
                      <li><strong>Username:</strong> The username associated with this credential (if applicable)</li>
                      <li><strong>Password/Secret:</strong> The actual sensitive data you're storing</li>
                      <li><strong>URL:</strong> The website or service URL (optional but recommended)</li>
                      <li><strong>Notes:</strong> Any additional context, instructions, or details</li>
                    </ul>
                    <p>Click <span className="px-2 py-1 bg-green-600 text-white rounded text-xs font-medium">Save Credential</span> to encrypt and store securely.</p>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4">
                    <span className="px-3 py-1.5 bg-gray-100 rounded-lg text-xs text-gray-600">🔑 Passwords</span>
                    <span className="px-3 py-1.5 bg-gray-100 rounded-lg text-xs text-gray-600">🔐 API Keys</span>
                    <span className="px-3 py-1.5 bg-gray-100 rounded-lg text-xs text-gray-600">🖥️ SSH Keys</span>
                    <span className="px-3 py-1.5 bg-gray-100 rounded-lg text-xs text-gray-600">🎫 Access Tokens</span>
                    <span className="px-3 py-1.5 bg-gray-100 rounded-lg text-xs text-gray-600">📝 Custom</span>
                  </div>
                </div>
              </div>

              {/* Step 5 */}
              <div className="relative flex gap-4 md:gap-6">
                <div className="w-12 h-12 bg-purple-600 text-white rounded-xl flex items-center justify-center font-bold text-lg flex-shrink-0 z-10 shadow-lg">5</div>
                <div className="flex-1 bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                    <FiLink className="w-5 h-5 text-purple-600" />
                    Link Vault to Your PTaaS Program
                  </h3>
                  <div className="mt-3 space-y-2 text-sm text-gray-600">
                    <p>Now that your vault contains credentials, you need to link it to a PTaaS program so pentesters can access them during security testing.</p>
                    <p><strong>You have two ways to link a vault:</strong></p>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3 mt-4">
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <p className="font-semibold text-green-800 mb-2">Option A: From Vault Page</p>
                      <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600">
                        <li>Go to the <strong>Credential Vault</strong> page</li>
                        <li>Find your vault in the list</li>
                        <li>Click the <span className="inline-flex items-center px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">🔗 Link</span> icon</li>
                        <li>Select your PTaaS program from the list</li>
                        <li>Set up the access password</li>
                      </ol>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg border">
                      <p className="font-semibold text-gray-900 mb-2">Option B: From Program Settings</p>
                      <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600">
                        <li>Navigate to your PTaaS Program page</li>
                        <li>Go to <strong>Settings</strong> tab</li>
                        <li>Find the <strong>Credential Vault</strong> section</li>
                        <li>Click <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">Link Vault</span></li>
                        <li>Select the vault and set access password</li>
                      </ol>
                    </div>
                  </div>
                    <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-100">
                      <p className="text-xs text-purple-700"><strong>📌 Note:</strong> You can link multiple vaults to one program if needed. Each link requires its own access password for isolation.</p>
                    </div>
                  </div>
                </div>

                {/* Step 6 - Important */}
                <div className="relative flex gap-4 md:gap-6">
                  <div className="w-12 h-12 bg-purple-600 text-white rounded-xl flex items-center justify-center font-bold text-lg flex-shrink-0 z-10 shadow-lg ring-4 ring-purple-200">6</div>
                  <div className="flex-1 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 shadow-sm border border-purple-200">
                    <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                      <FiUnlock className="w-5 h-5 text-purple-600" />
                      Set an Access Password
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-semibold">PROGRAM SPECIFIC</span>
                    </h3>
                    <div className="mt-3 space-y-2 text-sm text-gray-600">
                      <p>After linking the vault to your program, you'll be prompted to set an <strong>Access Password</strong>.</p>
                      <p className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-blue-800">
                        <strong>🔑 Key Point:</strong> The Access Password is DIFFERENT from your Master Password. It isolates this program's access from your main vault security.
                      </p>
                      <p><strong>What the Access Password does:</strong></p>
                      <ul className="list-disc list-inside ml-2 space-y-1">
                        <li>Encrypts the credentials specifically for this program link</li>
                        <li>Keeps your Master Password completely private and secure</li>
                        <li>Can be changed or revoked at any time without affecting your Master Password</li>
                      </ul>
                      <p>Enter a strong access password, confirm it, and click <span className="px-2 py-1 bg-purple-600 text-white rounded text-xs font-medium">Set Access Password</span>.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          {/* Quick Reference Card */}
          <div className="mt-8 bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-6 text-white">
            <div className="flex items-center gap-2 mb-4">
              <FiCheckCircle className="w-5 h-5 text-green-400" />
              <h3 className="font-bold text-lg">Quick Reference Summary</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div className="bg-white/10 rounded-lg p-4">
                <p className="text-blue-300 font-semibold mb-2">🔐 Master Password</p>
                <ul className="text-slate-300 text-xs space-y-1">
                  <li>• Your private encryption key</li>
                  <li>• Never share with anyone</li>
                  <li>• Cannot be recovered if lost</li>
                  <li>• Required to manage vault</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <p className="text-purple-300 font-semibold mb-2">🔑 Access Password</p>
                <ul className="text-slate-300 text-xs space-y-1">
                  <li>• Set during the linking process</li>
                  <li>• Isolates program access</li>
                  <li>• Required for program visibility</li>
                  <li>• Changeable at any time</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <p className="text-green-300 font-semibold mb-2">🚫 Revoke Access</p>
                <ul className="text-slate-300 text-xs space-y-1">
                  <li>• Unlink vault from program</li>
                  <li>• Access cut off instantly</li>
                  <li>• Organization-wide control</li>
                  <li>• All actions are logged</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Managing Vaults Section */}
        <section id="managing-vaults">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-purple-100 rounded-lg">
              <FiShield className="w-5 h-5 text-purple-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Managing Your Vaults</h2>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="divide-y divide-gray-200">
              <div className="p-5">
                <h4 className="font-semibold text-gray-900 mb-2">Creating a New Vault</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Click the <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-medium text-xs">+ New Vault</span> button
                  on the Credential Vault page. Give it a clear name and optional description.
                </p>
              </div>
              <div className="p-5">
                <h4 className="font-semibold text-gray-900 mb-2">Adding Credentials</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Expand any vault and click <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded font-medium text-xs">+ Add Credential</span>.
                  Select the credential type, fill in the details, and save. Your master password is required.
                </p>
              </div>
              <div className="p-5">
                <h4 className="font-semibold text-gray-900 mb-2">Editing & Deleting</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Hover over any credential or vault to see edit and delete options. Deleting a vault removes all credentials inside it.
                  <span className="text-amber-600 font-medium"> This action cannot be undone.</span>
                </p>
              </div>
              <div className="p-5">
                <h4 className="font-semibold text-gray-900 mb-2">Viewing Credentials</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Click on a credential entry to view its details. Sensitive fields like passwords are hidden by default —
                  click the eye icon to reveal them. All views are logged for security audit.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Access Controls Section */}
        <section id="access-controls">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-orange-100 rounded-lg">
              <FiUsers className="w-5 h-5 text-orange-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Access Controls & Permissions</h2>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
            <h3 className="font-semibold text-gray-900 mb-4">Who Can Do What?</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-900">Action</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-900">Business Owner</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-900">Organization Admin</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-900">Program Manager</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-900">Security Engineer</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-900">Pentester</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="py-3 px-4 text-gray-700">Create/Delete Vaults</td>
                    <td className="py-3 px-4 text-center text-green-600">✓</td>
                    <td className="py-3 px-4 text-center text-green-600">✓</td>
                    <td className="py-3 px-4 text-center text-red-400">✗</td>
                    <td className="py-3 px-4 text-center text-red-400">✗</td>
                    <td className="py-3 px-4 text-center text-red-400">✗</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="py-3 px-4 text-gray-700">Add/Edit Credentials</td>
                    <td className="py-3 px-4 text-center text-green-600">✓</td>
                    <td className="py-3 px-4 text-center text-green-600">✓</td>
                    <td className="py-3 px-4 text-center text-red-400">✗</td>
                    <td className="py-3 px-4 text-center text-red-400">✗</td>
                    <td className="py-3 px-4 text-center text-red-400">✗</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-gray-700">Link Vault to Program</td>
                    <td className="py-3 px-4 text-center text-green-600">✓</td>
                    <td className="py-3 px-4 text-center text-red-400">✗</td>
                    <td className="py-3 px-4 text-center text-red-400">✗</td>
                    <td className="py-3 px-4 text-center text-red-400">✗</td>
                    <td className="py-3 px-4 text-center text-red-400">✗</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="py-3 px-4 text-gray-700">View Shared Credentials</td>
                    <td className="py-3 px-4 text-center text-green-600">✓</td>
                    <td className="py-3 px-4 text-center text-green-600">✓</td>
                    <td className="py-3 px-4 text-center text-green-600">✓</td>
                    <td className="py-3 px-4 text-center text-green-600">✓</td>
                    <td className="py-3 px-4 text-center text-green-600">✓</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-gray-700">View Audit Logs</td>
                    <td className="py-3 px-4 text-center text-green-600">✓</td>
                    <td className="py-3 px-4 text-center text-green-600">✓</td>
                    <td className="py-3 px-4 text-center text-red-400">✗</td>
                    <td className="py-3 px-4 text-center text-red-400">✗</td>
                    <td className="py-3 px-4 text-center text-red-400">✗</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800 flex items-start gap-2">
                <span className="text-amber-500 mt-0.5">💡</span>
                <span>
                  <strong>Note:</strong> You can customize permissions for <strong>Organization Admin</strong> and <strong>Program Manager</strong> roles
                  when granting them access to a vault. This allows you to give more or fewer privileges based on your organization's needs.
                </span>
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-green-50 rounded-xl border border-green-200 p-5">
              <h4 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                <FiCheckCircle className="text-green-600" />
                Revoking Access
              </h4>
              <p className="text-sm text-green-800">
                You can revoke access at any time by unlinking the vault from the program.
                Pentesters will no longer be able to decrypt the credentials, even if they have the access password.
              </p>
            </div>
            <div className="bg-blue-50 rounded-xl border border-blue-200 p-5">
              <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                <FiEye className="text-blue-600" />
                Audit Trail
              </h4>
              <p className="text-sm text-blue-800">
                Every credential access is logged. You can view who accessed what and when in the Audit Trail tab.
                Failed decryption attempts are also logged for security monitoring.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-cyan-100 rounded-lg">
              <span className="text-xl">❓</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Frequently Asked Questions</h2>
          </div>

          <div className="space-y-3">
            <FAQItem
              question="What happens if I forget my master password?"
              answer={
                <div>
                  <p className="mb-2">Unfortunately, we cannot recover your master password or the credentials stored in your vault.
                  This is by design — it ensures that even we cannot access your sensitive data.</p>
                  <p className="text-amber-700 font-medium">⚠️ Store your master password securely. Consider using a password manager.</p>
                </div>
              }
            />
            <FAQItem
              question="How do I know if someone accessed my credentials?"
              answer="Every access attempt is logged in the Audit Trail. You can see which authorized researcher accessed what credentials, when they accessed them,
              and whether the access was successful."
            />
            <FAQItem
              question="What credential types are supported?"
              answer={
                <ul className="grid grid-cols-2 gap-1">
                  {["Password", "API Key", "SSH Key", "Certificate", "Database Credentials", "Server Credentials", "Secure Note", "WiFi Password", "Credit Card", "Custom"].map((type) => (
                    <li key={type} className="flex items-center gap-1"><span className="text-blue-500">•</span> {type}</li>
                  ))}
                </ul>
              }
            />
            <FAQItem
              question="Is there a limit to how many vaults or credentials I can create?"
              answer="There's no hard limit on the number of vaults or credentials. However, we recommend organizing credentials into
              logical vaults for better management. Too many credentials in a single vault can make it harder to manage."
            />
            <FAQItem
              question="What happens when I delete a vault?"
              answer={
                <div>
                  <p className="mb-2">Deleting a vault permanently removes all credentials inside it. Any active shares will become invalid,
                  and pentesters will no longer be able to access the credentials.</p>
                  <p className="text-red-700 font-medium">⚠️ This action cannot be undone. Make sure you really want to delete before confirming.</p>
                </div>
              }
            />
          </div>
        </section>

        {/* Learning Checklist */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-amber-100 rounded-lg">
              <FiCheckCircle className="w-5 h-5 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Track Your Progress</h2>
          </div>
          <LearningChecklist />
        </section>

        {/* Video Tutorial Section - Commented out for now */}
        {/* <section id="video">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-rose-100 rounded-lg">
              <FiPlay className="w-5 h-5 text-rose-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Video Tutorial</h2>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="aspect-video bg-slate-100 flex items-center justify-center">
              <iframe
                className="w-full h-full"
                src="about:blank"
                title="Credential Vault Tutorial"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ minHeight: "400px" }}
              />
            </div>
            <div className="p-4 bg-gray-50 border-t border-gray-200">
              <p className="text-sm text-gray-600 text-center">
                📹 Watch the complete walkthrough to learn how to set up and use the Credential Vault effectively.
              </p>
            </div>
          </div>
        </section> */}

        {/* Support Section */}
        <section className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 text-white text-center">
          <h2 className="text-2xl font-bold mb-3">Need More Help?</h2>
          <p className="text-blue-100 mb-6 max-w-xl mx-auto">
            If you have questions that aren't covered in this guide, our support team is here to help.
          </p>
          <div className="flex items-center justify-center gap-4">
            <a
              href="mailto:support@capturethebug.com"
              className="px-6 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
            >
              Contact Support
            </a>
            <Link
              to="/dashboard/vault"
              className="px-6 py-3 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-400 transition-colors"
            >
              Go to Credential Vault
            </Link>
          </div>
        </section>
      </div>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200 py-8 mt-16">
        <div className="max-w-5xl mx-auto px-6 text-center text-sm text-gray-500">
          <p>© {new Date().getFullYear()} Capture The Bug. All rights reserved.</p>
          <p className="mt-1">Credential Vault — Enterprise-grade security for your sensitive data.</p>
        </div>
      </footer>
    </div>
  );
};

export default CredentialVaultGuidePage;
